import React, { useCallback, useEffect, useRef, useState } from 'react';
import FileUpload from './components/FileUpload';
import ScanTypeReviewStep from './components/ScanTypeReviewStep';
import BatchProcessingView from './components/BatchProcessingView';
import TabularProcessingView from './components/TabularProcessingView';
import BatchResultsView from './components/BatchResultsView';
import TabularResultsView from './components/TabularResultsView';
import HistoryPanel from './components/HistoryPanel';
import Dashboard from './pages/Dashboard';
import AuthPage from './pages/AuthPage';
import ShareModal from './components/ShareModal';
import ReviewerDashboard from './pages/ReviewerDashboard';
import ReviewPage from './pages/ReviewPage';
import BusinessRules from './pages/BusinessRules';
import LifecyclePage from './pages/LifecyclePage';
import LandingPage from './pages/LandingPage';
import API from './services/api';
import './styles/App.css';

/** GET /results with retries to reduce spurious failures under batch load */
async function fetchResultsWithRetry(fileId) {
  const max = 3;
  let lastMsg = 'Failed to fetch results';
  for (let attempt = 0; attempt < max; attempt++) {
    try {
      const data = await API.getResults(fileId);
      return { fileId, data, error: null };
    } catch (err) {
      lastMsg = err?.error || lastMsg;
      if (attempt < max - 1) {
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
  }
  return { fileId, data: null, error: lastMsg };
}

/* ── Review URL detection — runs before any React state ──────────────────── */
const _reviewToken = window.location.pathname.startsWith('/review/')
  ? window.location.pathname.slice('/review/'.length).split('/')[0]
  : null;

/* ── Sidebar icons ───────────────────────────────────────────── */
function IconPlus() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}
function IconGrid() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  );
}
function IconClock() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}
function IconLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
    </svg>
  );
}
function IconSliders() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
      <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
      <line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/>
      <line x1="17" y1="16" x2="23" y2="16"/>
    </svg>
  );
}
function IconTimeline() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  );
}

/* ── Main App ────────────────────────────────────────────────── */
function App() {
  // ── ALL hooks unconditionally at the top ───────────────────
  const [authUser, setAuthUser] = useState(() => {
    const token    = localStorage.getItem('auth_token');
    const username = localStorage.getItem('auth_username');
    const email    = localStorage.getItem('auth_email');
    return token ? { token, username, email } : null;
  });

  const [userRole, setUserRole] = useState(
    () => localStorage.getItem('auth_role') || 'uploader'
  );

  const [showLanding,           setShowLanding]           = useState(true);
  const [landingRole,           setLandingRole]           = useState('uploader');
  const [pendingShareDrawingId, setPendingShareDrawingId] = useState(null);
  const [pendingShareFileName,  setPendingShareFileName]  = useState(null);
  const [showShareModal,        setShowShareModal]        = useState(false);
  const [approvalDone,          setApprovalDone]          = useState(false);

  // Survive browser refresh: the active page (and, below, an open results
  // view) is restored from sessionStorage instead of resetting to dashboard.
  const [currentView,   setCurrentView]   = useState(
    () => sessionStorage.getItem('ui_view') || 'dashboard'
  );
  const [currentStep,   setCurrentStep]   = useState('upload');
  const [dashboardKey,  setDashboardKey]  = useState(0);
  const [cancelledScan, setCancelledScan] = useState(null);

  // ── Parallel scan queue ─────────────────────────────────────
  // Each item: { fileId, fileName, ccSymbol, status, results, error }
  // status: 'processing' | 'completed' | 'failed'
  // All items start as 'processing' and complete independently.
  const [scanQueue, setScanQueue] = useState([]);

  // Files uploaded but awaiting scan-type review (smart scan step)
  const [pendingUploads, setPendingUploads] = useState([]);

  const [apiKey] = useState(
    import.meta.env.VITE_GEMINI_API_KEY ||
    localStorage.getItem('gemini_api_key') ||
    ''
  );
  const [error, setError] = useState(null);

  // Ref used only for the beforeunload guard (avoids stale closure)
  const scanQueueRef = useRef([]);
  useEffect(() => { scanQueueRef.current = scanQueue; }, [scanQueue]);

  const resultsPrefetchGenerationRef = useRef(0);

  // ── Derived ──────────────────────────────────────────────────
  const anyProcessing   = scanQueue.some(i => i.status === 'processing');
  const anyCompleted    = scanQueue.some(i => i.status === 'completed');
  const completedCount  = scanQueue.filter(i => i.status === 'completed').length;

  // allResultsReady: every row is terminal (no 'processing') and either failed with error
  // or completed with GET /results payload loaded (pipelines done + fetch succeeded for
  // each completed file). Gates "View Results" so BatchResultsView has full data immediately.
  const allResultsReady = scanQueue.length > 0 &&
    scanQueue.every((i) =>
      i.status === 'failed' ||
      (i.status === 'completed' && i.results != null)
    );

  // When all scans are terminal, batch-fetch results in parallel (one wave vs N sequential).
  useEffect(() => {
    const q = scanQueueRef.current;
    if (q.length === 0) return;
    const allTerminal = q.every((i) => i.status === 'completed' || i.status === 'failed');
    const idsNeedingPayload = q
      .filter((i) => i.status === 'completed' && i.results == null)
      .map((i) => i.fileId);
    if (!allTerminal || idsNeedingPayload.length === 0) return;

    const gen = ++resultsPrefetchGenerationRef.current;
    (async () => {
      const outs = await Promise.all(idsNeedingPayload.map((id) => fetchResultsWithRetry(id)));
      if (gen !== resultsPrefetchGenerationRef.current) return;

      setScanQueue((prev) => {
        const byId = new Map(outs.map((o) => [o.fileId, o]));
        return prev.map((item) => {
          const o = byId.get(item.fileId);
          if (!o) return item;
          if (item.results != null) return item;
          if (o.data) return { ...item, status: 'completed', results: o.data };
          return { ...item, status: 'failed', error: o.error, results: null };
        });
      });

      const firstOk = outs.find((o) => o.data);
      if (firstOk?.data) {
        setPendingShareDrawingId(firstOk.data.file_id || firstOk.fileId);
        setPendingShareFileName(firstOk.data.file_name || String(firstOk.fileId));
      }
      setApprovalDone(false);
    })();
  }, [scanQueue]);

  // Auto-navigation removed — user clicks "View Results" button in BatchProcessingView

  // ── Auth handlers ───────────────────────────────────────────
  const handleAuthSuccess = (data) => {
    API.invalidateCache();  // ensure no stale data from a previous session leaks into this user's view
    const role = data.role || 'uploader';
    setAuthUser(data);
    setUserRole(role);
    localStorage.setItem('auth_role', role);
  };

  const handleSignOut = () => {
    // Capture the current role before wiping localStorage so we can send the
    // user straight back to the auth page for their role (uploader → uploader
    // login, reviewer → reviewer login) rather than to the landing page.
    const roleBeforeLogout = localStorage.getItem('auth_role') || userRole || 'uploader';
    API.invalidateCache();  // clear all cached data so the next user never sees this user's data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_username');
    localStorage.removeItem('auth_email');
    localStorage.removeItem('auth_role');
    setAuthUser(null);
    setUserRole('uploader');
    setLandingRole(roleBeforeLogout);
    setShowLanding(false); // skip landing, go straight to auth page
  };

  // ── Tabular Scan state ──────────────────────────────────────
  // tabularScan: null | { fileId, fileName }
  const [tabularScan, setTabularScan] = useState(null);
  // tabularResults: null | { fileId, fileName, variants } — drives the dedicated
  // tabular result page (reached via the "View Results" button, like normal scan).
  const [tabularResults, setTabularResults] = useState(null);

  // Tabular scan now reuses the normal BATCH flow (1–10 files, concurrent).
  // Each file is a queue item flagged scanType:'tabular' so the processing card
  // starts it via /tabular-scan and the results render its per-part output.
  const handleTabularScan = (files) => {
    const arr = Array.isArray(files) ? files : [files];
    const queue = arr.map(f => ({
      fileId:    f.fileId,
      fileName:  f.fileName,
      ccSymbols: f.ccSymbols || [],
      batchId:   f.batchId || null,
      status:    'processing',
      results:   null,
      error:     null,
      scanType:  'tabular',
    }));
    resultsPrefetchGenerationRef.current += 1;
    setScanQueue(queue);
    setError(null);
    setTabularScan(null);
    setTabularResults(null);
    setCurrentView('workspace');
    setCurrentStep('processing');
  };

  const handleTabularBack = () => {
    setTabularScan(null);
    setTabularResults(null);
    setCurrentStep('upload');
  };

  // "View Results" on the tabular processing screen → navigate to the result page.
  const handleTabularViewResults = (variants) => {
    setTabularResults({
      fileId: tabularScan?.fileId,
      fileName: tabularScan?.fileName,
      variants: variants || [],
    });
    setCurrentStep('tabular-results');
  };

  /* ── Called by FileUpload when all files finish uploading ── */
  const handleFilesUploaded = (files) => {
    // files: [{ fileId, fileName, ccSymbols, batchId }]
    // Go to the review step so the user can confirm/override scan types.
    setPendingUploads(files);
    setError(null);
    setCurrentView('workspace');
    setCurrentStep('review');
  };

  /* ── Called by ScanTypeReviewStep when user confirms scan types ── */
  const handleScanConfirmed = (filesWithTypes) => {
    // filesWithTypes: [{ fileId, fileName, ccSymbols, batchId, scanType }]
    const queue = filesWithTypes.map(f => ({
      fileId:    f.fileId,
      fileName:  f.fileName,
      ccSymbols: f.ccSymbols || (f.ccSymbol ? [f.ccSymbol] : ['spade']),
      batchId:   f.batchId || null,
      scanType:  f.scanType || 'normal',
      status:    'processing',
      results:   null,
      error:     null,
    }));
    resultsPrefetchGenerationRef.current += 1;
    setScanQueue(queue);
    setPendingUploads([]);
    setError(null);
    setCurrentStep('processing');
  };

  /* ── Back from review step ── */
  const handleReviewBack = () => {
    setPendingUploads([]);
    setCurrentStep('upload');
  };

  /* ── Called by FileProgressCard when a file finishes successfully ── */
  const handleFileComplete = useCallback((fileId) => {
    setScanQueue((prev) =>
      prev.map((item) =>
        item.fileId === fileId ? { ...item, status: 'completed', results: null } : item
      )
    );
    setApprovalDone(false);

    // Eagerly fetch this file's results immediately — don't wait for all files to finish.
    // By the time the user clicks "View Results", most/all results are already loaded.
    (async () => {
      const out = await fetchResultsWithRetry(fileId);
      setScanQueue((prev) =>
        prev.map((item) => {
          if (item.fileId !== fileId) return item;
          if (item.results != null) return item; // already populated, don't overwrite
          if (out.data) return { ...item, status: 'completed', results: out.data };
          return { ...item, status: 'failed', error: out.error, results: null };
        })
      );
      if (out.data) {
        setPendingShareDrawingId(out.data.file_id || fileId);
        setPendingShareFileName(out.data.file_name || String(fileId));
      }
    })();
  }, []);

  /* ── Called by FileProgressCard when a file errors or is cancelled ── */
  const handleFileError = useCallback((fileId, msg) => {
    setScanQueue(prev => prev.map(item =>
      item.fileId === fileId ? { ...item, status: 'failed', error: msg } : item
    ));
  }, []);

  /* ── Cancel ALL: clears queue, returns to dashboard ── */
  const handleCancelAll = async () => {
    const confirmed = window.confirm(
      'Cancel all running scans? This will stop every file currently being processed.'
    );
    if (!confirmed) return;

    // Fire-and-forget cancel requests for all processing items
    const processing = scanQueueRef.current.filter(i => i.status === 'processing');
    await Promise.allSettled(processing.map(i => API.cancelProcessing(i.fileId)));

    const snap = processing.length > 0 ? {
      id:               processing[0].fileId,
      file_name:        processing[0].fileName,
      status:           'failed',
      cc_count:         0,
      item_count:       0,
      upload_timestamp: new Date().toISOString(),
      completed_timestamp: null,
    } : null;

    setScanQueue([]);
    setError(null);
    setCurrentStep('upload');
    if (snap) setCancelledScan(snap);
    setDashboardKey(k => k + 1);
    setCurrentView('dashboard');
  };

  // Warn before browser close/refresh when any scan is active
  useEffect(() => {
    if (currentStep !== 'processing') return;
    const handler = (e) => {
      if (!scanQueueRef.current.some(i => i.status === 'processing')) return;
      e.preventDefault();
      e.returnValue = 'Scans are still running. Closing now will not stop them on the server.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [currentStep]);

  // approvalPending is true whenever there are completed scans in the results view
  // and the user hasn't submitted an approval decision yet.
  const approvalPending = !approvalDone && currentStep === 'results' && anyCompleted;

  const guardedNavigate = (action) => {
    if (approvalPending) {
      const confirmed = window.confirm(
        'You haven\'t submitted an approval decision for this scan yet.\n\nYou can come back to this scan from your Dashboard or History at any time.\n\nLeave without submitting approval?'
      );
      if (!confirmed) return;
    }
    action();
  };

  const handleNewUpload = () => {
    // If a scan is actively running, redirect to it instead of resetting
    if (currentStep === 'processing' && scanQueue.length > 0) {
      setCurrentView('workspace');
      return;
    }
    guardedNavigate(() => {
      setScanQueue([]);
      setPendingUploads([]);
      setError(null);
      setCurrentView('workspace');
      setCurrentStep('upload');
      setPendingShareDrawingId(null);
      setPendingShareFileName(null);
      setApprovalDone(false);
      setShowShareModal(false);
    });
  };

  const openHistoryPage   = () => guardedNavigate(() => { setCurrentView('history');        setError(null); });
  const openDashboard     = () => guardedNavigate(() => { setCurrentView('dashboard');      setError(null); });
  const openBusinessRules = () => guardedNavigate(() => { setCurrentView('business-rules'); setError(null); });
  const openLifecycle     = () => guardedNavigate(() => { setCurrentView('lifecycle');      setError(null); });

  // ── Refresh persistence ────────────────────────────────────────────────────
  // Keep the active page in sessionStorage so F5 refreshes THIS page instead
  // of resetting to the dashboard. An open results view additionally saves its
  // file ids so the results (persisted server-side) are re-fetched on load.
  useEffect(() => {
    sessionStorage.setItem('ui_view', currentView);
  }, [currentView]);

  useEffect(() => {
    if (currentStep === 'results' && scanQueue.length > 0) {
      sessionStorage.setItem('ui_scan', JSON.stringify({
        step: 'results',
        files: scanQueue.map(q => ({ fileId: q.fileId, fileName: q.fileName })),
      }));
    } else if (currentStep === 'processing' && scanQueue.length > 0) {
      // Mid-scan: persist the full queue so an F5 returns to the processing
      // view. FileProgressCard resumes safely — it checks server status first
      // and only polls scans that are already running (never double-starts).
      sessionStorage.setItem('ui_scan', JSON.stringify({
        step: 'processing',
        files: scanQueue.map(q => ({
          fileId: q.fileId, fileName: q.fileName, ccSymbols: q.ccSymbols,
          batchId: q.batchId || null, scanType: q.scanType || 'normal',
        })),
      }));
    } else if (currentStep === 'review' && pendingUploads.length > 0) {
      // Scan-type review step: persist the uploaded files so an F5 returns
      // here (detection re-runs from the server-side files, nothing is lost).
      sessionStorage.setItem('ui_scan', JSON.stringify({
        step: 'review',
        files: pendingUploads.map(f => ({
          fileId: f.fileId, fileName: f.fileName,
          ccSymbols: f.ccSymbols, batchId: f.batchId || null,
        })),
      }));
    } else if (currentStep === 'upload') {
      sessionStorage.removeItem('ui_scan');
    }
  }, [currentStep, scanQueue, pendingUploads]);

  // Snapshot the saved scan ONCE at mount — the save-effect above clears
  // ui_scan while currentStep is still the initial 'upload', which would
  // otherwise delete the mid-scan state before restore could read it.
  const [savedScanSnapshot] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('ui_scan') || 'null'); } catch { return null; }
  });

  useEffect(() => {
    if (!authUser) return;
    const saved = savedScanSnapshot;
    if (saved && saved.step === 'results' && Array.isArray(saved.files) && saved.files.length > 0) {
      Promise.all(saved.files.map(async (f) => ({
        fileId: f.fileId,
        fileName: f.fileName,
        status: 'completed',
        results: await API.getResults(f.fileId),
      })))
        .then((items) => {
          setScanQueue(items);
          // Restored items already carry their results, so the share-target
          // prefetch effect never runs — set the approval target here too,
          // otherwise "Ask for Approval" opens with Drawing #null.
          const first = items.find(i => i.results);
          if (first) {
            setPendingShareDrawingId(first.results.file_id || first.fileId);
            setPendingShareFileName(first.results.file_name || first.fileName || String(first.fileId));
          }
          setCurrentView('workspace');
          setCurrentStep('results');
        })
        .catch(() => { /* results gone — stay on the restored view */ });
    } else if (saved && saved.step === 'processing' && Array.isArray(saved.files) && saved.files.length > 0) {
      // Refresh happened mid-scan: rebuild the queue and drop straight back
      // into the processing view. Each card re-attaches to its scan via
      // getProcessStatus (running → poll; completed → finish; else restart).
      setScanQueue(saved.files.map(f => ({
        fileId:    f.fileId,
        fileName:  f.fileName,
        ccSymbols: f.ccSymbols || ['spade'],
        batchId:   f.batchId || null,
        scanType:  f.scanType || 'normal',
        status:    'processing',
        results:   null,
        error:     null,
      })));
      setCurrentView('workspace');
      setCurrentStep('processing');
    } else if (saved && saved.step === 'review' && Array.isArray(saved.files) && saved.files.length > 0) {
      // Refresh on the scan-type review step: restore the uploaded files and
      // reopen the review — detection re-runs against the stored files.
      setPendingUploads(saved.files.map(f => ({
        fileId:    f.fileId,
        fileName:  f.fileName,
        ccSymbols: f.ccSymbols || ['spade'],
        batchId:   f.batchId || null,
      })));
      setCurrentView('workspace');
      setCurrentStep('review');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  // ── Guard: /review/{token} — render ReviewPage regardless of auth ──────────
  if (_reviewToken) {
    return (
      <ReviewPage
        token={_reviewToken}
        authUser={authUser}
        onAuthSuccess={handleAuthSuccess}
      />
    );
  }

  // ── Guard: show landing page, then auth page when not logged in ────────
  if (!authUser) {
    if (showLanding) {
      return (
        <LandingPage
          onGetStarted={(role = 'uploader') => {
            setLandingRole(role);
            setShowLanding(false);
          }}
        />
      );
    }
    return (
      <AuthPage
        onAuthSuccess={handleAuthSuccess}
        onBack={() => setShowLanding(true)}
        initialRole={landingRole}
      />
    );
  }

  // ── Guard: reviewer role routes to ReviewerDashboard ────────────────────
  if (userRole === 'reviewer') {
    return (
      <ReviewerDashboard
        authUser={authUser}
        onSignOut={handleSignOut}
        onAuthSuccess={handleAuthSuccess}
      />
    );
  }

  /* ── Breadcrumb label ── */
  const breadcrumbPage =
    currentView === 'dashboard'      ? 'Dashboard'      :
    currentView === 'history'        ? 'History'        :
    currentView === 'business-rules' ? 'Business Rules' :
    currentView === 'lifecycle'      ? 'Audit Log'      :
    currentStep === 'review'             ? 'Review Scan Types' :
    currentStep === 'processing'        ? 'Processing' :
    currentStep === 'tabular-processing' ? 'Processing' :
    currentStep === 'results'           ? 'Results'    :
    currentStep === 'tabular-results'   ? 'Results'    : 'New Analysis';

  return (
    <div className="app">

      {/* ── Share Modal ── */}
      {showShareModal && (() => {
        const completedItems = scanQueue.filter(i => i.status === 'completed');
        const sharedBatchId  = completedItems.length > 0 ? completedItems[0].batchId : null;
        const isBatchShare   = !!(sharedBatchId && completedItems.length > 1);
        return (
          <ShareModal
            drawingId={isBatchShare ? null : pendingShareDrawingId}
            batchId={isBatchShare ? sharedBatchId : null}
            fileCount={isBatchShare ? completedItems.length : null}
            drawingName={isBatchShare ? `Batch (${completedItems.length} files)` : pendingShareFileName}
            onDone={() => {
              setShowShareModal(false);
              setApprovalDone(true);
              setDashboardKey(k => k + 1);
            }}
            onCancel={() => setShowShareModal(false)}
          />
        );
      })()}

      <div className="app-main">

        {/* ══ SIDEBAR ═══════════════════════════════════════════════ */}
        <aside className="primary-sidebar">

          {/* Brand */}
          <div className="sidebar-brand">
            <div className="brand-icon"><IconLogo /></div>
            <div>
              <h2>InSpectly.AI</h2>
              <p>Engineering Intelligence</p>
            </div>
          </div>

          {/* CTA */}
          <div className="sidebar-cta">
            <button className="sidebar-cta-btn" onClick={handleNewUpload}>
              <IconPlus />
              New Analysis
            </button>
          </div>

          {/* Nav */}
          <div className="nav-section">
            <div className="nav-section-label">Workspace</div>
            <button
              className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
              onClick={openDashboard}
            >
              <span className="nav-item-icon"><IconGrid /></span>
              <span>Dashboard</span>
            </button>
            <button
              className={`nav-item ${currentView === 'history' ? 'active' : ''}`}
              onClick={openHistoryPage}
            >
              <span className="nav-item-icon"><IconClock /></span>
              <span>History</span>
            </button>
            <button
              className={`nav-item ${currentView === 'business-rules' ? 'active' : ''}`}
              onClick={openBusinessRules}
            >
              <span className="nav-item-icon"><IconSliders /></span>
              <span>Business Rules</span>
            </button>
            <button
              className={`nav-item ${currentView === 'lifecycle' ? 'active' : ''}`}
              onClick={openLifecycle}
            >
              <span className="nav-item-icon"><IconTimeline /></span>
              <span>Audit Log</span>
            </button>

            {currentStep === 'processing' && anyProcessing ? (
              <button
                className={`nav-item ${currentView === 'workspace' ? 'active' : ''}`}
                onClick={() => setCurrentView('workspace')}
                title="Scans running in parallel — click to view"
              >
                <span className="nav-item-icon"><span className="scan-pulse-dot" /></span>
                <span>
                  Scanning…
                  {scanQueue.length > 0 && (
                    <span className="nav-badge">
                      {completedCount}/{scanQueue.length}
                    </span>
                  )}
                </span>
              </button>
            ) : currentStep === 'results' && anyCompleted && currentView !== 'workspace' ? (
              <button
                className="nav-item scan-done-nav"
                onClick={() => setCurrentView('workspace')}
                title="Scans complete — click to view results"
              >
                <span className="nav-item-icon"><span className="scan-done-dot" /></span>
                <span>
                  Results Ready ✓
                  {scanQueue.length > 1 && (
                    <span className="nav-badge green">
                      {completedCount}/{scanQueue.length}
                    </span>
                  )}
                </span>
              </button>
            ) : currentView === 'workspace' && (
              <button
                className="nav-item active"
                onClick={() => {}}
                style={{ cursor: 'default' }}
              >
                <span className="nav-item-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <span>Current Scan</span>
              </button>
            )}
          </div>

          {/* Footer: user row + sign out */}
          <div className="sidebar-footer">
            <div className="sidebar-footer-stat">
              <span className="sidebar-footer-label">Total scans</span>
              <span className="sidebar-footer-num" id="sidebar-total-scans">—</span>
            </div>
            <div className="sidebar-user-row">
              <div className="sidebar-user-avatar">
                {authUser.username?.[0]?.toUpperCase() ?? 'U'}
              </div>
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">{authUser.username}</span>
                <span className="sidebar-user-email">{authUser.email}</span>
              </div>
              <button className="sidebar-signout-btn" onClick={handleSignOut} title="Sign out">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </div>
          </div>
        </aside>

        {/* ══ WORKSPACE ═══════════════════════════════════════════════ */}
        <section className="workspace">

          {/* Top bar */}
          <header className="app-header">
            <div className="header-breadcrumb">
              <span className="header-breadcrumb-root">InSpectly.AI</span>
              <span className="header-breadcrumb-sep">/</span>
              <span className="header-breadcrumb-current">{breadcrumbPage}</span>
            </div>
            <div className="header-actions">
              <button className="header-btn-ghost" onClick={openDashboard}>Dashboard</button>
              <button className="header-btn-ghost" onClick={openHistoryPage}>History</button>
              <button className="header-btn-primary" onClick={handleNewUpload}>
                <IconPlus /> New Run
              </button>
            </div>
          </header>

          {/* ── Content ── */}
          <div className="content">

            {error && (
              <div className="error-alert">
                <span>{error}</span>
                <button onClick={() => setError(null)}>×</button>
              </div>
            )}

            {/* Dashboard */}
            {currentView === 'dashboard' && (
              <Dashboard
                forceRefresh={dashboardKey}
                onGoToWorkspace={handleNewUpload}
                optimisticFailed={cancelledScan}
                onDataLoaded={() => setCancelledScan(null)}
              />
            )}

            {/* History */}
            {currentView === 'history' && (
              <div className="history-page-shell">
                <HistoryPanel onBackToWorkspace={() => setCurrentView('workspace')} />
              </div>
            )}

            {/* Business Rules */}
            {currentView === 'business-rules' && (
              <BusinessRules />
            )}

            {/* Lifecycle */}
            {currentView === 'lifecycle' && (
              <LifecyclePage />
            )}

            {/* Upload */}
            {currentView === 'workspace' && currentStep === 'upload' && (
              <div className="step-upload">
                <FileUpload
                  onFilesUploaded={handleFilesUploaded}
                  onTabularScan={handleTabularScan}
                  onError={(msg) => setError(msg)}
                />
              </div>
            )}

            {/* Scan Type Review — smart scan step between upload and processing */}
            {currentView === 'workspace' && currentStep === 'review' && pendingUploads.length > 0 && (
              <ScanTypeReviewStep
                uploadedFiles={pendingUploads}
                apiKey={apiKey}
                onConfirm={handleScanConfirmed}
                onBack={handleReviewBack}
              />
            )}

            {/* Processing — always mounted while scanning so polling survives navigation;
                hidden via display:none when the user is on a different page */}
            {currentStep === 'processing' && scanQueue.length > 0 && (
              <div style={{ display: currentView === 'workspace' ? 'block' : 'none' }}>
                <BatchProcessingView
                  queue={scanQueue}
                  apiKey={apiKey}
                  onComplete={handleFileComplete}
                  onError={handleFileError}
                  onCancelAll={handleCancelAll}
                  onViewResults={() => setCurrentStep('results')}
                  allResultsReady={allResultsReady}
                />
              </div>
            )}

            {/* Tabular Scan processing view */}
            {currentView === 'workspace' && currentStep === 'tabular-processing' && tabularScan && (
              <TabularProcessingView
                fileId={tabularScan.fileId}
                fileName={tabularScan.fileName}
                apiKey={apiKey}
                onBack={handleTabularBack}
                onViewResults={handleTabularViewResults}
              />
            )}

            {/* Tabular result page — reached via the "View Results" button */}
            {currentView === 'workspace' && currentStep === 'tabular-results' && tabularResults && (
              <div className="tabular-result-page">
                <div className="fps-strip">
                  <div className="fps-summary">
                    <div className="fps-summary-counts">
                      <span className="fps-total">Tabular Inspection Plan</span>
                      <span className="fps-count completed">
                        <span className="fps-dot completed">✓</span>
                        {tabularResults.variants.length} part{tabularResults.variants.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <button className="fps-cancel-all" onClick={handleTabularBack}>
                      ← Back to Upload
                    </button>
                  </div>
                  <div className="fps-pills">
                    <button className="fps-pill completed active" title={tabularResults.fileName}>
                      <span className="fps-pill-dot completed">✓</span>
                      <span className="fps-pill-name">{tabularResults.fileName}</span>
                    </button>
                  </div>
                </div>
                <TabularResultsView
                  fileId={tabularResults.fileId}
                  variants={tabularResults.variants}
                />
              </div>
            )}

            {/* Results — scrollable list of all completed files */}
            {currentView === 'workspace' && currentStep === 'results' && (
              <BatchResultsView
                queue={scanQueue}
                apiKey={apiKey}
                approvalPending={approvalPending}
                approvalDone={approvalDone}
                onShareClick={() => setShowShareModal(true)}
                onResultUpdated={(fileId, updated) => {
                  setScanQueue(prev => prev.map(item =>
                    item.fileId === fileId ? { ...item, results: updated } : item
                  ));
                }}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default App;
